/**
 * THE LIVE CAVEATS, IN ONE PLACE, IN PLAIN LANGUAGE.
 *
 * =====================================================================================
 * WHY THIS FILE EXISTS, AND THE EDITORIAL RULE IT ENFORCES
 *
 * The panels are CURRENT STATE, not the history of how we got here. Retraction archaeology
 * ("this panel previously said X", "corrected on <date>", defect numbers like F1/A6/A13 cited
 * inline) belongs in `docs/DECISION-HISTORY.md` and `docs/FINDINGS-ADVERSARIAL-REVIEW.md`,
 * where a reader who wants the audit trail can find it. A customer reading this page needs to
 * know what is true now.
 *
 * ⚠️ BUT A LIVE CAVEAT IS CURRENT STATE. An unmeasured claim, an unproven feature or a figure
 * that is a lower bound is not history — it is a fact about the system as it stands today, and
 * removing it would be the difference between decluttering and overstating. So every caveat
 * that is still LIVE lives here, in words rather than in defect-number shorthand, and is
 * rendered beside the figure or the feature it qualifies.
 *
 * ONE COPY, because the way two copies of a caveat drift is that one gets softened while the
 * other stays strict — leaving a page that argues both sides of its own honesty. And a single
 * export is greppable: `scripts/verify-web-live.sh` asserts several of these strings render in
 * the live DOM, so softening one fails a check rather than passing quietly.
 *
 * =====================================================================================
 * ⚠️ THE POSTAL OPERATOR IS STILL NAMED IN THIS FILE, ON PURPOSE. DO NOT STRIP IT.
 *
 * The interface was de-identified: the page heading, the document title and the footer no longer
 * name the customer, and the Apigee caveat above was rewritten to drop the attribution. A blanket
 * find-and-replace over this file would look like finishing that job. It would instead make the
 * site LESS accurate and would weaken its most important safety warning. Three deliberate keeps:
 *
 *   1. `SIMULATION.neverPrint` and `SIMULATION.midsAreFabricated` — THE DO-NOT-MAIL WARNING.
 *      "not recognised by USPS" and "not operated by a licensed USPS mailer" are the sentences that
 *      stop someone tendering one of these barcodes as real mail. The organisation that would fail
 *      to recognise the barcode, and that issues the Mailer ID this demo does not hold, is a
 *      SPECIFIC organisation — "not recognised by the postal operator" is vaguer and therefore
 *      weaker, and a reader could take it for a generic disclaimer. Anonymity is not worth trading
 *      against the one warning on this site whose failure mode is real mail in a real network.
 *   2. `USPS2000508` — the specification's own document number, cited for provenance. The spec IS
 *      that organisation's published document; renaming it would misattribute a public standard and
 *      make the provenance claim uncheckable.
 *   3. `UC4_FK3_RESIDUAL`'s "not a real USPS code" — the Service Type Code registry is that
 *      organisation's. The risk is precisely that an undefined code is structurally valid, and
 *      naming whose registry defines "defined" is what makes the sentence mean anything.
 *
 * The distinction being applied: WHO THE CUSTOMER IS is removed, because that is theirs to
 * disclose. WHOSE PUBLIC SPECIFICATION THIS IMPLEMENTS stays, because that is a citation, and
 * WHOSE MAIL NETWORK MUST NEVER SEE THESE BARCODES stays, because that is a safety warning.
 * =====================================================================================
 */

/** Latency: what the number is measured at, and what sits on top of it. */
/*
 * ⚠️ THE ATTRIBUTION WAS DROPPED HERE, THE SUBSTANCE WAS NOT.
 *
 * This used to name the customer as the operator of the Apigee tier. The interface no longer names
 * them anywhere, so this sentence would have been the one place it leaked. What MATTERS about the
 * caveat is that the figure excludes a hop the CUSTOMER owns and we cannot measure — so it is now
 * "the customer's own Apigee hop", which carries the identical warning: the number on screen is not
 * the end-to-end number their callers will see, and the budget has to cover both.
 */
export const LATENCY_CLIENT_SIDE =
  'Latency is a browser- or client-observed round trip measured at the AWS front door. It ' +
  'EXCLUDES the Apigee hop the customer operates in front of this API — Apigee’s own processing ' +
  'and network cost sits on top of every figure here, and the sub-200ms (in places sub-100ms) ' +
  'budget has to cover both.';

/**
 * Every latency figure on the site is a WARM-FLEET figure, and a cold start is not a rounding
 * error against this customer's budget. Carried across from the in-region benchmark panel because
 * it changes how the p50 beside it may be quoted, not because it is a separate result.
 */
export const WARM_FLEET_ONLY =
  'Every latency figure here is measured against a WARMED fleet. A Lambda cold start is a large ' +
  'fraction of — and can exceed — the customer’s entire round-trip budget on its own, before ' +
  'Apigee is added, so a warm p50 is not what a spike arriving at an idle fleet will see. That is ' +
  'the strongest argument in the whole benchmark for provisioned concurrency or a keep-warm ' +
  'strategy.';

/** Throughput: why every rps figure is a floor and not a ceiling. */
export const RPS_LOWER_BOUND =
  'Every requests-per-second figure is a LOWER BOUND on the service. The API Gateway front ' +
  'door sheds load before the service does, and the load generators were deliberately capped ' +
  'below the lower region’s burst bucket so the two regions stay comparable — so none of these ' +
  'runs found the maximum the design can serve. Both halves of that still hold. The regional ' +
  'burst asymmetry that motivated the cap is now HISTORICAL — the two regions were verified ' +
  'symmetric after an AWS support case raised the smaller quota — but the figures shown here ' +
  'were measured under the cap and keep that label rather than being restated, and the front ' +
  'door still sheds before the service does regardless of the quota.';

/** The two 429s are different events. Summing them is how a front-door limit gets read as a design limit. */
export const TWO_KINDS_OF_429 =
  'The two 429 columns are NEVER summed. An app 429 carries code "EXHAUSTED": the request ' +
  'reached Lambda and the allocator refused it, so it is the only throttle that says anything ' +
  'about the buffer or the serial range. A bare 429 is the API Gateway front door rejecting the ' +
  'request before Lambda ran, so it bounds only the offered load.';

/** Reclamation: shipped, disarmed, and never exercised against a live serial. */
export const RECLAMATION_OFF =
  'Reclamation ships OFF and has NEVER reclaimed a serial in production. The machinery is ' +
  'built, tested and deployed disabled — enabling it is a parameter change, not a deployment — ' +
  'but until it runs live, the 120-day capacity ceiling is what applies: each MID/STC ' +
  'combination has a finite pool and nothing returns aged-out serials to it.';

/** UC1 is consume-once, and it is a design decision rather than a gap. */
export const UC1_CONSUME_ONCE =
  'Serials from POST /v1/serials are CONSUME-ONCE BY DESIGN. UC1 writes no eligibility ' +
  'evidence, and eligibility is built entirely from ledger rows — so reclamation is a UC2/UC4 ' +
  'property, because that is where the ledger row is written. A UC1 serial is permanently spent.';

/** UC4's zero-outage claim: read, not measured. */
export const UC4_R8_READ_NOT_MEASURED =
  'The zero-outage-during-patching claim is READ FROM AWS DOCUMENTATION, NOT MEASURED HERE. ' +
  'Proving it means driving load through a schema change and showing zero errors, which this ' +
  'repo did not do. The strongest evidence we have is circumstantial: DSQL refuses the blocking ' +
  'form of CREATE INDEX outright, so the non-blocking path is the only path.';

/**
 * =====================================================================================
 * THE FOUR CROSS-CUTTING FACTS THE RECOMMENDATION RATIONALE MAY NOT BE READ WITHOUT.
 *
 * These live here rather than in RationalePanel.jsx for the reason this file exists: each one
 * qualifies a claim made in more than one place, and the way two copies of a qualifier drift is
 * that one gets softened while the other stays strict. `scripts/verify-web-live.sh` asserts all
 * four in the rendered DOM, so softening one fails a check rather than passing quietly.
 *
 * ⚠️ AND THE FIRST THREE ARE A SET. Publishing "UC7 was not chosen" without the second is
 * misleading in UC4's favour, and publishing either without the third invites the reader to think
 * the comparison is settled when one customer answer moves it. NO FIGURE APPEARS IN ANY OF THEM —
 * every number that would belong here is measured in documents that can be checked, and a
 * hand-typed figure from this site has already become a wrong number in a customer's own written
 * analysis. The rationale is qualitative and does not need them.
 * =====================================================================================
 */

/**
 * UC7 was BUILT, then MEASURED, then ruled out — in that order, which is what makes this evidence.
 *
 * ⚠️ THE WINS ARE STATED BEFORE THE LOSS, ON PURPOSE. A rejected option summarised only by its
 * defects reads as a straw man, and a reader who later discovers UC7 was the faster of the two
 * discounts everything else on the page. Its advantages are real and measured; it loses anyway,
 * on one axis, for reasons no configuration reaches.
 */
export const UC7_BUILT_AND_MEASURED_THEN_NOT_CHOSEN =
  'UC7 WAS BUILT AND MEASURED BEFORE IT WAS RULED OUT — evidence, not a paper judgement. Driven ' +
  'through a live engine patch, a managed switchover and an unplanned failover. It genuinely ' +
  'WINS on read latency in both regions, on database-enforced integrity that survived a failover, ' +
  'on losing no acked write, and on a server-side statement_timeout UC4 cannot have. It LOSES on ' +
  'availability: under A-U1 (OUR ASSUMPTION, pending the customer’s answer) it failed R8 on all ' +
  'three events, and the remediation research found the switchover blackout and R7 both ' +
  'INTRINSIC — no configuration reaches either. ⚠️ AND IT IS NOT OUR DEFECT: the outage was ' +
  'Aurora refusing connections throughout (ECONNREFUSED — a prompt TCP RST, so a fresh attempt ' +
  'actively refused, not our client holding a dead socket), and Aurora declared the switchover ' +
  'COMPLETE while the demoted region went on refusing.';

/**
 * ⚠️ THE EVEN-HANDED LINE. It must survive every future edit of the rationale.
 *
 * The asymmetry is not a technicality: UC7 has been held to a standard its competitor has never
 * been asked to meet, because DSQL exposes no operation to run the test against. Recording UC7's
 * outage as a cost is correct. Reading UC4's silence as a zero is the exact error this project has
 * already had to correct in writing.
 */
export const MEASURED_COST_VS_UNMEASURED_UNKNOWN =
  'UC7’s OUTAGES ARE A MEASURED COST; UC4’s ARE AN UNMEASURED UNKNOWN. UC4’s R8 claim has never ' +
  'been driven — DSQL exposes no schedulable patch, switchover or failover event, so the ' +
  'equivalent test is not feasible against it. That is recorded BOTH as a real UC7 disadvantage ' +
  'AND as an unmeasured unknown for UC4, NOT as a measured zero for UC4. Nothing here may imply ' +
  'UC4 passed a test it has never taken.';

/**
 * R14 is the hinge, and saying so is what stops the page reading as though measurement settled it.
 *
 * The rules-layer comparison turns on whether database-enforced integrity is worth what it looks
 * like. R14 is the customer's own answer to that, and it points at "less than it looks like".
 */
export const R14_IS_THE_HINGE =
  'R14 IS THE HINGE — THAT ONE CUSTOMER ANSWER MOVES THE RECOMMENDATION MORE THAN ANY MEASUREMENT ' +
  'ON THIS SITE. The customer states rules are ALREADY enforced at application level today, ' +
  'because their label writes are asynchronous. If that holds, UC4’s biggest cost — six integrity ' +
  'guarantees permanently in application code — is a guarantee they are not relying on, and ' +
  'UC7’s biggest gain is worth less than it appears. Confirming it is one email.';

/**
 * ⚠️ THE FRAMING WITHOUT WHICH THIS PAGE MISLEADS BY OMISSION.
 *
 * A page whose most prominent section is the rules-layer choice implies the rules-layer choice is
 * THE decision. It is not. The binding constraint is a property of the barcode format, no option
 * on this site touches it, and it is answerable by the customer in one email.
 *
 * NO FIGURE, deliberately — the per-pool ceilings are computed by the live service and read from
 * GET /v1/pools on the Capacity tab, which is where a checkable number belongs.
 */
export const RULES_LAYER_IS_NOT_THE_BINDING_PROBLEM =
  '⚠️ THE RULES-LAYER CHOICE IS NOT THE CUSTOMER’S BINDING PROBLEM, and a recommendation page that ' +
  'reads as though it were would mislead. The binding question is U4: whether any of the seven ' +
  'highest-volume MID/STC combinations sits on a 7-digit construct. A 7-digit serial field under a ' +
  '120-day uniqueness window caps BELOW the daily volume stated for those combinations — and that ' +
  'ceiling is a property of the BARCODE FORMAT, so it holds at any throughput, on any cloud, with ' +
  'any engine. All four options share UC1’s issuance unchanged, so none of them moves it. U4 is ' +
  'one question to the customer and it outranks every measurement here.';

/** UC4's sharpest residual risk, stated in the words that matter rather than as "FK-3". */
export const UC4_FK3_RESIDUAL =
  'Aurora DSQL enforces no foreign keys at all, so an undefined 3-digit Service Type Code is ' +
  'caught only by the resolving query’s INNER JOIN. Nothing stops a writer that bypasses the ' +
  'application module from storing one, and a Service Type Code that is not a real USPS code ' +
  'still produces a structurally valid barcode that means the wrong thing.';

/**
 * =====================================================================================
 * UC7 — DEPLOYED, MEASURED, AND NOT CHOSEN. THE THIRD STATE ARRIVED.
 *
 * ⚠️ THESE STRINGS HAVE NOW BEEN CORRECTED TWICE, AND THE PATTERN IS THE LESSON.
 *
 * Round one: they said UC7 was "A PLAN, NOT A DEPLOYMENT" and that "NOTHING ABOUT UC7 HAS BEEN
 * MEASURED". Both went false when UC7 was built, and were narrowed to "deployed, and not measured".
 * Round two — THIS ONE: "not measured" has itself gone false. All six measurements in the
 * evaluation ran. UC7 was driven through a live minor-version engine patch, a managed switchover
 * and an unplanned `--allow-data-loss` failover with a caller watching in both regions at 250 ms.
 *
 * Leaving "not measured" up would be the same defect a second time, and this file's header names
 * it: a stale caveat renders perfectly, every presence assertion keeps passing, and the page
 * states something untrue while looking scrupulous. THE STATE MACHINE COMPLETED:
 *   PLANNED   — was UC7. No longer.
 *   DEPLOYED  — it is, and it vends in both regions.
 *   MEASURED  — IT NOW IS. And the measurement is why it was NOT CHOSEN.
 *
 * ⚠️ THE HARDEST EDITORIAL PROBLEM ON THIS TAB IS NOW THE OPPOSITE OF WHAT IT WAS. It used to be
 * "do not let a deployment read as a benchmark". It is now "do not let a benchmark read as a
 * recommendation". UC7 measured WELL on latency and BADLY on availability, and the badly is what
 * decided the outcome. A panel leading with "faster than UC4" and filing three outage windows
 * further down would be advocacy wearing evidence's clothes. So the VERDICT leads, and the
 * evidence then runs in both directions.
 *
 * ⚠️ AND THE NO-FIGURES RULE FOR THESE STRINGS IS UNCHANGED — no number appears in any of them.
 * That is not because figures are forbidden on the tab now; the measured figures ARE on the tab,
 * derived at build time by web/uc7-eval-summary.mjs from the committed per-sample probe JSON, and
 * a re-run that contradicts a claim made here fails the build. It is because a figure typed into
 * THIS file could be checked by nothing. Values live where a parser puts them.
 * =====================================================================================
 */

/** UC7's status, stated before anything else it claims — verdict first, not latency first. */
export const UC7_MEASURED_AND_NOT_CHOSEN =
  'UC7 IS DEPLOYED, MEASURED, AND WAS NOT CHOSEN. All three states are now settled, and the third ' +
  'is the one that matters: UC7 was not ruled out on paper, it was BUILT, then DRIVEN through a ' +
  'live minor-version engine patch, a managed switchover and an unplanned --allow-data-loss ' +
  'failover, with a caller watching in BOTH regions throughout. It WINS on the resolving read, on ' +
  'losing no acked write, and on database-enforced integrity that survived a failover. It LOSES on ' +
  'AVAILABILITY, and that is what decided the recommendation: under A-U1 (OUR ASSUMPTION, pending ' +
  'the customer’s answer on what "zero outages" means) every one of the three events cost ' +
  'caller-visible read availability, and two produced windows in which NO region anywhere could ' +
  'serve a rules read. It is a GENUINE TRADE that loses on the axis the customer named — not a ' +
  'straw man, and not a winner.';

/**
 * The measured availability result, and WHOSE it is.
 *
 * ⚠️ THE ATTRIBUTION IS HALF THE POINT. The convenient finding would have been that we handicapped
 * UC7 and its R8 failure was partly self-inflicted — a client-side defect is cheap to fix and would
 * soften a finding that reflects badly on a candidate architecture. The forensics refuted that: the
 * errno distinguishes "we held a dead socket" from "the server refused us", and it says the server.
 */
export const UC7_OUTAGES_MEASURED_AND_NOT_OUR_DEFECT =
  'EVERY TOPOLOGY EVENT COST CALLER-VISIBLE READ AVAILABILITY, and the switchover — the PLANNED, ' +
  'rehearsed, RPO-0 operation AWS documents as controlled — was the worst single-region case AND ' +
  'produced a window with both regions dark at once. ⚠️ AND IT IS NOT OUR DEFECT: Aurora refused ' +
  'connections throughout, reported as ECONNREFUSED — a prompt TCP RST, meaning a FRESH connection ' +
  'attempt was ACTIVELY REFUSED rather than our client blocking on a dead socket, which the ' +
  'sub-second spacing between failures independently confirms. Aurora declared the switchover ' +
  'COMPLETE while the demoted region went on refusing reads for roughly another three quarters of ' +
  'a minute: "switchover completed" does NOT mean "the demoted region serves reads again". No ' +
  'change to our client would have altered any of it, which STRENGTHENS the R8 verdict rather than ' +
  'softening it.';

/**
 * Which flaws could be engineered away and which could not — the distinction that decides whether
 * a measured flaw is a reason to rule an option out or merely a reason to do more work.
 */
export const UC7_INTRINSIC_VS_SURMOUNTABLE =
  'THE TWO FLAWS THAT DECIDED THIS ARE INTRINSIC; the rest are surmountable, and separating them is ' +
  'what makes the rejection defensible. SURMOUNTABLE: the patch outage (the secondary’s reader is ' +
  'categorically excluded from zero-downtime patching, so a second reader gives the reader endpoint ' +
  'somewhere else to route); the per-failed-read latency (our own connection timeout); the ' +
  'connection cliff (see below — it was our artefact, not Aurora’s limit); no credential rotation ' +
  '(customer-managed rotation); and the missing IAM control over DDL (IAM database authentication ' +
  'is already enabled, so UC4’s posture is reachable). ⛔ INTRINSIC, with no configuration reaching ' +
  'either: the SWITCHOVER BLACKOUT, because AWS documents that DB instances in ALL regions restart ' +
  'and the new writer is a promoted reader OF THE TARGET CLUSTER — so the region being promoted ' +
  'must lose its reader to gain its writer, and more readers SHRINK the window without deleting ' +
  'it; and R7, because the primary region holds the ONLY writer instance and a secondary’s writer ' +
  'endpoint is inactive. Every alternative was checked and ruled out with documentation. The only ' +
  'candidate that passes R7 is DSQL multi-region — which is UC4’s engine.';

/**
 * ⚠️ THE ARTEFACT THAT MUST NOT BE PUBLISHED AS AN AURORA CEILING.
 *
 * The evaluation measured a hard connection cliff and attributed it to Aurora's `max_connections`.
 * The remediation research found the number was the 4-ACU band's default, and that the evaluation
 * had raised max ACU to 8 with "no instance restart" — which is exactly the condition under which a
 * STATIC parameter does not update. So the cliff sat two doublings below where the documented
 * formula puts it. Publishing it as Aurora's limit would defame the engine with our own oversight.
 */
export const UC7_CONNECTION_CLIFF_WAS_OUR_ARTEFACT =
  'THE CONNECTION CLIFF WAS OUR OWN ARTEFACT, NOT AN AURORA CEILING, and it must not be reported as ' +
  'one. Past the limit requests failed with a 500 rather than shedding gracefully, which is a real ' +
  'property worth knowing — but the limit reached was the value for the FOUR-ACU capacity band, ' +
  'while the evaluation had already raised maximum capacity to eight. max_connections derives from ' +
  'MAXIMUM ACU and is a STATIC parameter requiring an instance REBOOT, and the capacity change was ' +
  'made with no instance restart — precisely the condition under which it does not take effect. So ' +
  'the cliff sat two doublings below where the documented formula puts it, one reboot closes it, ' +
  'and at the documented eight-ACU value the database would no longer be the first bound at all. ' +
  '⚠️ It was also never the binding constraint: the clean measured ceiling is more than an order of ' +
  'magnitude above the customer’s entire aggregate envelope.';

/**
 * The two operational defects the evaluation FOUND BY DRIVING IT, both now fixed and verified live.
 *
 * ⚠️ RECORDED AS FIXED RATHER THAN DELETED. A defect found and closed is evidence the build was
 * driven hard enough to find it; silently removing it would make the page look as though nothing
 * had gone wrong, which is a different and less trustworthy claim.
 */
export const UC7_ROLE_AND_FORWARDING_DEFECTS_FIXED =
  'TWO OPERATIONAL DEFECTS WERE FOUND BY DRIVING THE SWITCHOVER, AND BOTH ARE NOW FIXED AND ' +
  'VERIFIED LIVE. First: the roles swapped and /v7 did not notice, because globalRole came from a ' +
  'DEPLOY-TIME environment variable and a switchover happens entirely outside the deploy — so every ' +
  'response in both regions asserted a topology that no longer existed, with the authority of a ' +
  'provenance field built to be trustworthy. It is now DERIVED AT RUNTIME from the database’s own ' +
  'pg_is_in_recovery(), exactly as readEndpoint already was, and each response reports roleSource ' +
  'so the derivation is visible rather than trusted. Second: write forwarding did not follow the ' +
  'role change, leaving the new secondary refusing writes outright — it is now PRE-ARMED on BOTH ' +
  'members, which is what AWS documents the flag for: on a primary the setting does nothing until ' +
  'that primary is demoted, and then applies immediately. ⚠️ Verify it on the REQUESTED field, not ' +
  'the status field: status stays absent on a writer even when the pre-arm has taken, so checking ' +
  'status alone gives a false negative.';

/**
 * The concrete counterpart to UC4's six moved guarantees — WITH R14 in the same breath.
 *
 * ⚠️ THE TWO HALVES MAY NOT BE SEPARATED, and this is the strongest instance of that rule on the
 * whole site. "The database enforces integrity again" is UC7's headline gain over UC4. R14 is the
 * customer's own statement that rules are ALREADY enforced at application level today — which is
 * the strongest available argument that the gain closes a gap they do not experience as one. A page
 * that prints the gain and files the objection further down is a sales sheet.
 */
export const UC7_REFERENTIAL_INTEGRITY_DB_ENFORCED =
  'Every /v7 response reports referentialIntegrity: "database-enforced", and that is the concrete ' +
  'counterpart to UC4, where six integrity guarantees live in Lambda permanently with no path to ' +
  'push them back down. Foreign keys, CHECK constraints, ON DELETE RESTRICT and PL/pgSQL all ' +
  'exist on this engine and the seeded schema uses them. ⚠️ AND THE COUNTER-ARGUMENT, IN THE SAME ' +
  'BREATH: R14 — the customer states rules are ALREADY enforced at application level today, ' +
  'because their label writes are asynchronous. So this closes a gap they may not experience as ' +
  'one. It is worth arguing in either direction and it is not worth assuming.';

/**
 * A server-side bound UC4 structurally cannot have, discovered by driving it rather than reading.
 *
 * ⚠️ THE TIMEOUT'S VALUE IS DELIBERATELY NOT PRINTED, and the omission is the no-figures rule
 * applied rather than an oversight. The bound is a configured setting on the deployed transport,
 * and NO endpoint this page calls reports it — so a number here could only be hand-typed, which is
 * precisely the shape of defect that put a wrong figure into a customer's own written analysis.
 * What matters architecturally is that Aurora ACCEPTS the mechanism and DSQL REFUSES it, and that
 * claim needs no number at all.
 */
export const UC7_STATEMENT_TIMEOUT =
  'Aurora PostgreSQL ACCEPTS a SERVER-SIDE statement_timeout, and this was proven by setting it ' +
  'live on both members and reading it back from the server rather than by reading a document. ' +
  'Aurora DSQL REFUSES THE CONNECTION OUTRIGHT when statement_timeout is sent (SQLSTATE 0A000), ' +
  'so UC4 can only set a CLIENT-side query_timeout — which abandons the WAIT while the statement ' +
  'keeps running and keeps consuming capacity server-side. statement_timeout actually CANCELS the ' +
  'statement. So a slow query stops burning capacity instead of piling up behind a client that ' +
  'stopped listening, and UC7 gets a server-side bound UC4 structurally cannot have. The timeout’s ' +
  'VALUE is not printed here on purpose: no endpoint this page calls reports it, so a figure could ' +
  'only be hand-typed.';

/**
 * ⚠️ A FINDING THAT CUTS AGAINST UC7, AND IT IS ON THE PAGE FOR THAT REASON.
 *
 * A tab listing only an option's advantages is advocacy. This one was not predicted by any planning
 * document, it was found by attempting the thing, and it doubled UC7's projected standing cost.
 */
export const UC7_MANAGED_PASSWORD_INCOMPATIBLE =
  'AURORA GLOBAL DATABASE IS INCOMPATIBLE WITH THE RDS-MANAGED MASTER PASSWORD — measured, not ' +
  'read: the API refuses outright with "ManageMasterUserPassword is not supported for an Aurora ' +
  'global database", for a brand-new member as well as for adopting an existing cluster. The ' +
  'deployed rules clusters use the RDS-managed password deliberately, so that the credential is ' +
  'never expressed in infrastructure code at all and RDS owns its ROTATION. Adopting one of them ' +
  'as the global primary would have meant deleting the managed secret that UC2 currently ' +
  'authenticates with — a live use case — so it was refused and a separate pair of clusters was ' +
  'built instead. UC7 therefore holds a SELF-MANAGED password with NO automatic rotation, and it ' +
  'costs an extra cluster nobody planned for. This is a point AGAINST UC7 and FOR UC4.';

/**
 * ⚠️ THE SECOND FINDING THAT CUTS AGAINST UC7. The credential axis does not favour it.
 *
 * Stated as an ordering rather than a grievance, because an ordering is checkable: UC4 has no
 * password at all, UC2 has one the Data API fetches on its behalf, UC7 must hold one in process.
 */
export const UC7_CREDENTIAL_AXIS =
  'ON THE CREDENTIAL AXIS THE ORDER IS UC4 > UC2 > UC7, AND UC7 IS LAST. UC4 has NO password at ' +
  'all — it authenticates with short-lived IAM tokens. UC2 has an Aurora password but the RDS ' +
  'Data API fetches it, so the value never enters the Lambda process. UC7 speaks the PostgreSQL ' +
  'wire protocol, so it must hold the password in memory to hand to the driver — and the ' +
  'credential it reads is the MASTER credential. There is no IAM-shaped control withholding DDL ' +
  'from the UC7 runtime, because a PostgreSQL connection is authorised by the DATABASE and not by ' +
  'IAM: UC4’s dsql:DbConnect-to-rules_reader mapping makes runtime DDL STRUCTURALLY IMPOSSIBLE, ' +
  'and UC7 has no equivalent today. The route to closing it is named rather than implied — IAM ' +
  'database authentication is already enabled on both members, so a SELECT-only rules_reader role ' +
  'with rds_iam would reach UC4’s posture, and that GRANT must run on the PRIMARY because GRANT ' +
  'is not forwarded.';

/**
 * ⚠️ THE OVERSTATEMENT THIS PROJECT ALREADY MADE ONCE, IN WRITING, AND HAD TO CORRECT.
 *
 * Global write forwarding lets a SECONDARY cluster accept a write. It does not let the secondary
 * EXECUTE one. Every forwarded write runs on the single primary. "A write endpoint in every
 * region" and "a writer in every region" are one word apart and a whole requirement apart, and
 * blurring them is how R7 gets marked met when it is not.
 */
export const UC7_R7_NOT_MET =
  'R7 (active-active at all layers) is NOT MET IN LETTER by UC7. Global write forwarding gives a ' +
  'write ENDPOINT per region, NOT a WRITER per region: every forwarded write executes on the one ' +
  'primary. Lose the primary region and writes STOP until a failover promotes a new one. The ' +
  'discriminating test is not whether a second region can accept a write — forwarding gives it ' +
  'that — but whether it can still accept one with the primary region gone. It cannot. ' +
  'AND THIS IS NOW MEASURED RATHER THAN READ: two rows written THROUGH the us-west-2 secondary ' +
  'were then read back FROM the us-east-1 primary, and the secondary’s own database reports ' +
  'pg_is_in_recovery() = true while transaction_read_only = off — the session is writable only ' +
  'because the forwarding shim makes it so, on a cluster that is genuinely a replica. Every /v7 ' +
  'response from the secondary region says so in its own words, and a test asserts the phrase ' +
  '"active-active" appears NOWHERE in any /v7 response.';

/** DDL is the R8 case exactly, not an adjacent inconvenience. */
export const UC7_DDL_NOT_FORWARDED =
  'DDL IS NOT FORWARDED, and this is now MEASURED against the live secondary rather than read: ' +
  'CREATE TABLE, ALTER TABLE, DROP TABLE and CREATE INDEX were each refused with SQLSTATE 25006 ' +
  'and an explicit "not supported for write forwarding" reason, with forwarding ENABLED. So schema ' +
  'changes must be issued against the primary directly and there is no per-region schema autonomy; ' +
  'a managed switchover or failover additionally requires primary and secondary on the same major ' +
  'AND minor engine version. That is precisely the case R8 names when it says "zero outages, not ' +
  'even for patching" — so patching is not a side issue for UC7, it is the requirement itself. ' +
  '⚠️ AND THE PATCH HAS NOW BEEN DRIVEN AGAINST UC7 UNDER LOAD, which no option on this site had ' +
  'previously been asked to survive: a routine minor-version engine upgrade took the SECONDARY ' +
  'region’s reads down while the PRIMARY sailed through, because zero-downtime patching covers the ' +
  'writer but is EXPLICITLY excluded for a reader in a global secondary. It also opened a version-' +
  'skew window during which the managed failover path was, per its own documented precondition, ' +
  'unavailable — the cluster is LESS resilient while being patched, which is the opposite of what ' +
  'R8 asks. The equivalent test is NOT feasible against UC4, so UC4’s patching behaviour remains ' +
  'an unmeasured unknown rather than a measured zero.';

/**
 * Under A-U1, a switchover with RPO zero is still an outage. Both halves must stay together.
 *
 * ⚠️ THE LAST SENTENCE USED TO READ "READ FROM AWS DOCUMENTATION, not drilled here". IT IS NOW
 * DRILLED, and the drill is worse than the documentation implied: the all-region restart the docs
 * describe cost the longest single-region outage of the whole evaluation plus a window with no
 * healthy region anywhere. Leaving the old provenance label up would have UNDERSTATED a measured
 * finding while appearing cautious — the rarer direction of the same defect, and just as wrong.
 */
export const UC7_SWITCHOVER_IS_STILL_AN_OUTAGE =
  'A planned SWITCHOVER syncs every secondary first, so its RPO is zero — and it RESTARTS ' +
  'INSTANCES IN ALL REGIONS while it runs. Zero data loss is not zero unavailability, and under ' +
  'A-U1 (OUR ASSUMPTION, pending the customer’s answer on what "zero outages" means) a window ' +
  'that rejects a write IS an outage for that write. MANUAL promotion is a different operation ' +
  'again and is IRREVERSIBLE: replication into that cluster stops permanently and it can never ' +
  're-attach to the global database. ⚠️ THIS IS NO LONGER READ FROM DOCUMENTATION — IT WAS DRILLED, ' +
  'and it came out WORSE than the documentation implied: the switchover produced the LONGEST ' +
  'single-region read outage measured in the whole evaluation, plus a window during which NEITHER ' +
  'region could serve a rules read. The managed switchover and failover paths do both PRESERVE the ' +
  'topology, which was confirmed by driving them — only manual promotion is the one-way door.';

/**
 * The transport collision — VERIFIED by driving it, and the code change it forced is now WRITTEN.
 *
 * ⚠️ THE STATED REASON WAS WRONG AND WAS CORRECTED, WHILE THE VERDICT STOOD. The planning
 * documents said the Data API fails because a secondary "has no cluster or writer endpoint, only a
 * reader endpoint". Driven: the secondary DOES have a cluster- DNS name, it resolves, it accepts
 * connections, and it accepts forwarded writes. So the verdict was right and its justification was
 * false — and a justification a reader can disprove in one command discredits everything beside it.
 * The reader endpoint is used because it is the endpoint AWS documents for a secondary and the one
 * guaranteed to keep meaning that across a promotion, not because it is the only one that exists.
 */
export const UC7_DATA_API_INCOMPATIBLE =
  'The RDS Data API DOES NOT WORK against a global-database secondary, and this was VERIFIED ' +
  'rather than trusted: SELECT count(*), a bare SELECT 1 and an INSERT all failed with ' +
  'DatabaseUnavailableException, while the IDENTICAL call pattern against the primary SUCCEEDED — ' +
  'so it is not a credential, IAM, ARN or configuration problem, and the Data API is ENABLED on ' +
  'both clusters. The deployed rules API uses the Data API, so UC7 needed a different transport ' +
  'for the secondary-region read path. That transport is now BUILT (a single pg connection per warm ' +
  'execution environment, a bounded recycle timer, reconnect on a dropped socket, and the role the ' +
  'connection actually reached reported back from the transport itself rather than from config). ' +
  '⚠️ AND ITS SHARPEST HAZARD IS THE ERROR’S NAME: DatabaseUnavailableException reads as ' +
  'transient, but against a secondary the condition is PERMANENT until promotion, so a naive retry ' +
  'wrapper would retry forever against something that can never succeed. It is classified as ' +
  'terminal, and a test asserts it is ABSENT from the retryable list — because the natural "fix" ' +
  'for a future reader is to add it there, which is exactly backwards.';

/** Constructs that exist in the barcode layer but are unreachable through either engine. */
export const C05_C06_UNREACHABLE =
  'C05 and C06 exist in the barcode layer but have NO seeded rules, so the rules engine cannot ' +
  'resolve to them. UC1 was narrowed to C01–C04 + C07 to match, so it can no longer vend them ' +
  'either — the API answers 400. Nothing a customer can do is lost: C05 shares its serial pool ' +
  'with C07, and C06’s wider space has never been asked for.';

/**
 * What none of the load-test runs exercised. Carried across from the benchmark panels' coverage
 * sections: a benchmark that reads as complete when it is not is worse than one that admits its
 * edges, and "no failure injection" is the edge that matters most for a two-region design.
 */
export const NO_FAILURE_INJECTION =
  'No failure was injected during the LOAD RUNS: no Valkey failover, no Aurora failover, no ' +
  'throttle-storm recovery and no partition. So these runs measure the two regions’ steady-state ' +
  'INDEPENDENCE, not their failover. A regional outage HAS since been induced separately, in a ' +
  'routing drill described below — but not while a load run was in flight, so no run here shows ' +
  'how the surviving region behaves under the other’s traffic.';

/**
 * ⚠️ NARROWED, NOT DELETED, AND THE TWO HALVES MUST STAY TOGETHER.
 *
 * A region-failure drill HAS now been run, and the old text ("NO region-failure drill has ever been
 * run") was therefore false on a published page. But the drill proved the ROUTING half only: the
 * shared name withdrew the failed region and a caller redirected across regions received a serial
 * from the survivor's own disjoint range. It did NOT prove the CAPACITY half — no survivor has been
 * watched carrying a doubled rate, and no pool's serial capacity has been tested under one.
 *
 * Stating only the first half would be WORSE than the stale understatement it replaces, because a
 * reader would take "failover drilled" for "region loss is handled" — and for the pool nearest its
 * ceiling the runway arithmetic says exactly the opposite. So both halves are in one string, and
 * neither may be trimmed without the other.
 *
 * NO FIGURE HERE, on purpose: the runway numbers are computed per pool by the live service and read
 * from GET /v1/pools on the Capacity tab. Retyping one into this file is the defect this project has
 * already shipped once.
 */
/**
 * THE ARCHITECTURE TAB'S BLUF — the recommendation and the two unmeasured things, up front.
 *
 * ⚠️ WHY THIS EXISTS. The Architecture tab is the tab a technical reader opens first for
 * orientation, and it opened straight into a diagram: it said WHICH option was recommended and
 * nothing about why, or about what is still unmeasured. Both of those live further down the same
 * page, below the fold, which is where an adversarial readability audit found most of this site's
 * conclusions sitting.
 *
 * ⚠️ THIS SUMMARISES AND POINTS. IT IS NEVER THE ONLY COPY. NO_OUTAGE_DRILL and RECLAMATION_OFF
 * keep rendering verbatim and in full further down that panel, including NO_OUTAGE_DRILL's closing
 * clause "failover ALONE cannot save that pool". A BLUF that becomes the only copy of a caveat has
 * quietly shortened the caveat.
 *
 * ⚠️ AND IT SAYS NOTHING ABOUT UC4 PATCHING, SCHEMA CHANGE, SWITCHOVER OR ZERO-OUTAGE. A compressed
 * BLUF reaching for "UC4 patches with no outage" would assert a test UC4 NEVER TOOK — Aurora DSQL
 * exposes no schedulable patch, switchover or failover event, so the equivalent drill is not
 * feasible against it. UC4's R8 advantage is an UNMEASURED UNKNOWN, never a measured zero, and
 * UC4_R8_READ_NOT_MEASURED is the only place that may be characterised. That exact edit was
 * proposed during the audit and rejected by a verifier; it is written down here so it is not
 * proposed a second time.
 *
 * Wording that must survive any future pass: the literal words "NEVER been measured" and
 * "ARITHMETIC", and no softening to "not yet fully measured" / "largely measured" / "expected to" /
 * "should". "REJECTED" never appears without the availability reason in the same sentence.
 */
export const ARCH_BLUF =
  'Recommendation: UC1 serial issuance plus UC4 rules, two regions active-active — because each ' +
  'region owns a disjoint half of every serial range, so a partition between regions costs ' +
  'latency and never a duplicate tracking number. Two things on this page are NOT measured, and ' +
  'both are stated in full below. Failover CAPACITY has NEVER been measured: no region has been ' +
  'taken to zero and no survivor has been watched absorbing double load, so every survivor-runway ' +
  'figure is ARITHMETIC only — and for the pool nearest its ceiling that runway is SHORTER than ' +
  'the lead time to mint a fresh Mailer ID, so failover alone cannot save it. Reclamation ships ' +
  'OFF and has never reclaimed a serial, so the 120-day pool ceiling applies. UC7 was measured ' +
  'and REJECTED — not untested, and not passed: every topology event cost caller-visible read ' +
  'availability.';

export const NO_OUTAGE_DRILL =
  'The ROUTING half of region failure is drilled live and works: one region’s health check was ' +
  'inverted, the shared hostname withdrew it, and a vend issued during the simulated outage ' +
  'returned a serial from the SURVIVOR’s own disjoint range — structurally incapable of ' +
  'colliding, with no coordination and no replication. The CAPACITY half has NEVER been measured: ' +
  'no region has been taken to zero, no survivor has been watched absorbing the other’s load for ' +
  'any length of time, and no pool’s serial capacity has been tested under a doubled rate. So ' +
  'every survivor-runway figure remains ARITHMETIC over a topology verified by reading code — and ' +
  'that arithmetic is not reassuring: for the pool nearest its ceiling the runway is shorter than ' +
  'the lead time for minting a fresh Mailer ID, so failover ALONE cannot save that pool.';

/**
 * /v1/pools coverage, restated after task #70 MEASURED it rather than assumed it.
 *
 * ⚠️ THE OLD TEXT SAID "a pool vended solely through POST /v1/serials can be absent". That was
 * true and is now FALSE: registration moved onto the refill path via the system-of-record store,
 * and a live check across both regions listed 4 of 4 pools (it listed 1 of 4 before). Leaving
 * the old sentence up would make the site UNDER-claim, which is its own kind of inaccuracy — so
 * the caveat is NARROWED to the one path that genuinely still cannot register, and the numbers
 * are named so a reader can check them rather than take this on trust.
 */
export const POOLS_UNPROVEN =
  'GET /v1/pools coverage was MEASURED on 2026-07-31: 4 of 4 pools visible in each region ' +
  '(us-east-1 and us-west-2), against 1 of 4 before the registry write was moved off the ' +
  'reclamation path. It is STILL a LOWER BOUND, and this is the reason: the registry is written ' +
  'when a pool REFILLS a block, so the A6 buffer bypass — which vends straight from the ' +
  'high-water mark and never refills — never registers. A pool vended EXCLUSIVELY through ' +
  'X-Bypass-Buffer is absent from the listing, and is still fully assessable by naming it ' +
  'directly. No alarm built on this endpoint has ever fired.';

/** The rate /v1/pools reports understates exactly the pool you most want to catch. */
export const POOLS_RATE_IS_LIFETIME_MEAN =
  'The rate is a LIFETIME MEAN, not a trailing 30-day rate, so it understates a pool that ' +
  'recently ramped up — which is the pool you most want to catch. A trailing window needs ' +
  'periodic mark snapshots, which are not built.';

/**
 * THE RECOMMENDATION, IN ONE LINE, FOR THE TOP OF EVERY TAB.
 *
 * ⚠️ WHY THIS EXISTS. An adversarial readability audit of all nine tabs found the site's verdict
 * was only reachable on the SIXTH tab (Findings), after four option tabs — so a reader working
 * left to right met four detailed options before learning that one was recommended and one was
 * rejected. The rationale itself was judged succinct and well-argued; it was simply undiscoverable.
 * This line is the pointer, rendered above the tab strip on every tab. It does NOT replace
 * RationalePanel, which remains the citation.
 *
 * ⚠️ "MEASURED" AND "NOT CHOSEN" TRAVEL TOGETHER, ALWAYS. A bare "UC7 was measured" reads as
 * endorsed, and UC7 was measured and REJECTED — four rows on its own tab argue against it.
 * Never soften "NOT CHOSEN" to "not preferred", and never upgrade "measured" to "validated",
 * "tested", "passed" or "proven": those claim a bar was met, and the availability bar was not.
 *
 * ⚠️ IT MUST RENDER BELOW THE SIMULATION BANNER. The banner is legally load-bearing and stays
 * first on the page from every scroll position; a recommendation is never allowed to outrank it.
 */
export const SITE_VERDICT =
  'Recommendation: UC1 serial issuance plus UC4 rules. UC7 was deployed and MEASURED, and was ' +
  'NOT CHOSEN — every topology event cost caller-visible read availability. Full reasoning, ' +
  'including what is still unmeasured, on the Findings & recommendation tab.';

/** The simulation warning, in one place, because it is the one that must never soften. */
export const SIMULATION = {
  header: 'SIMULATION — NOT VALID FOR MAILING',
  midsAreFabricated:
    'Mailer IDs are FABRICATED. This demo is not operated by a licensed USPS mailer and holds no ' +
    'Mailer ID assignment.',
  neverPrint:
    'The barcode STRUCTURE is spec-accurate (Barcode, Package, Intelligent Mail® Specification ' +
    'Rev E, USPS2000508, §8.5), but the barcodes produced here MUST NEVER be printed on, or used ' +
    'to tender, real mail. They will not track and are not recognised by USPS.',
};
